import type { FeedProviderStatus } from '@fruuit/shared'
import { UiLink } from '@fruuit/ui'
import { ArrowRight } from 'lucide-react'
import { HealthBadge, RunStatusBadge } from './badges'
import { formatDateTime, formatPercent, formatRelative, plural } from './format'
import { RunLegend, RunSparkline } from './RunSparkline'

/** "Data sources" card on the admin Overview: status, last/next run, 7-day timeline. */
export function ProviderCard({ provider }: { provider: FeedProviderStatus }) {
  const last = provider.last_run
  return (
    <article
      aria-labelledby={`provider-${provider.id}`}
      className="flex min-w-0 flex-col rounded-lg border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(22_62_58/0.04)]"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={`provider-${provider.id}`} className="font-semibold leading-snug text-ink">
            {provider.name}
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-sage">{provider.licence_label}</p>
        </div>
        <HealthBadge health={provider.health} />
      </header>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="min-w-0">
          <dt className="text-xs font-semibold text-sage">Last run</dt>
          <dd className="mt-1">
            {last ? (
              <span className="flex flex-col items-start gap-1">
                <RunStatusBadge status={last.status} />
                <span className="text-xs text-sage" title={formatDateTime(last.started_at)}>
                  {formatRelative(last.finished_at ?? last.started_at)}
                </span>
              </span>
            ) : (
              <span className="text-sage">No runs yet</span>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs font-semibold text-sage">Next run</dt>
          <dd className="mt-1 text-ink">
            {provider.next_due_at ? (
              <>
                <span className="block">{formatRelative(provider.next_due_at)}</span>
                <span className="text-xs text-sage">{provider.schedule_label}</span>
              </>
            ) : (
              <span className="text-sage">Not scheduled</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-sage">Success, 7 days</dt>
          <dd className="mt-1 font-semibold tabular-nums text-ink">
            {formatPercent(provider.success_rate_7d)}
            <span className="ml-1 text-xs font-normal text-sage">
              of {plural(provider.runs_7d, 'run')}
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-sage">Open alerts</dt>
          <dd
            className={`mt-1 font-semibold tabular-nums ${provider.open_alerts > 0 ? 'text-attention' : 'text-ink'}`}
          >
            {provider.open_alerts}
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-line-soft pt-4">
        <p className="mb-2 text-xs font-semibold text-sage">Runs, last 7 days</p>
        <RunSparkline days={provider.days} label={provider.name} className="max-w-full" />
        <RunLegend className="mt-2" />
      </div>

      <UiLink
        href={`/admin/feeds?provider=${encodeURIComponent(provider.id)}`}
        className="mt-4 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-teal hover:underline"
      >
        Run history
        <ArrowRight aria-hidden="true" className="size-4" />
      </UiLink>
    </article>
  )
}
